/**
 * Formatea una fecha a una cadena legible
 * @param {Date|string} date - Fecha a formatear
 * @returns {string} Cadena de fecha formateada
 */
export const formatDate = (date) => {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Formatea una fecha y hora a una cadena legible
 * @param {Date|string} date - Fecha a formatear
 * @returns {string} Cadena de fecha y hora formateada
 */
export const formatdateWithTime = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleString('es-MX', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

/**
 * Trunca el texto si excede una cierta longitud
 * @param {string} text - Texto a truncar
 * @param {number} maxLength - Longitud máxima antes de truncar
 * @returns {string} Texto truncado con puntos suspensivos o texto original
 */
export const truncateText = (text, maxLength = 100) => {
  if (!text || text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Pone en mayúscula la primera letra de una cadena
 * @param {string} string - Cadena a capitalizar
 * @returns {string} Cadena con la primera letra en mayúscula
 */
export const capitalizeFirstLetter = (string) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
};

/**
 * Genera un ID aleatorio
 * @returns {string} ID aleatorio
 */
export const generateId = () => {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15);
};

/**
 * Formatea un número de teléfono al formato (+52) 000-000-00-00
 * @param {string} phoneNumber - El número de teléfono a formatear (ej. "+521234567890" o "1234567890")
 * @returns {string} El número de teléfono formateado o el original si es inválido.
 */
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return '';
  }

  // Limpiar el número, dejando solo los dígitos.
  const digitsOnly = phoneNumber.replace(/\D/g, '');

  // Extraer el código de país y el número local.
  let countryCode = '52';
  let localNumber = '';

  if (digitsOnly.startsWith('52') && digitsOnly.length === 12) {
    localNumber = digitsOnly.substring(2);
  } else if (digitsOnly.length === 10) {
    localNumber = digitsOnly;
  } else {
    // Si el formato no es de 10 o 12 dígitos, devolver el original.
    return phoneNumber;
  }

  // Aplicar el formato al número local de 10 dígitos -> 000-000-00-00
  const areaCode = localNumber.substring(0, 3);
  const firstPart = localNumber.substring(3, 6);
  const secondPart = localNumber.substring(6, 8);
  const thirdPart = localNumber.substring(8, 10);

  return `(+${countryCode}) ${areaCode}-${firstPart}-${secondPart}-${thirdPart}`;
};

/**
 * Función debounce para limitar la frecuencia con la que se llama a una función
 * @param {Function} func - Función a la que aplicar debounce
 * @param {number} wait - Milisegundos de espera
 * @returns {Function} Función con debounce aplicado
 */
export const debounce = (func, wait = 300) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Filtra un array de objetos basado en un término de búsqueda
 * @param {Array} array - Array a filtrar
 * @param {string} searchTerm - Término a buscar
 * @param {Array} keys - Claves del objeto en las que buscar
 * @returns {Array} Array filtrado
 */
export const filterArrayBySearchTerm = (array, searchTerm, keys) => {
  if (!searchTerm || !array || !keys || !Array.isArray(array) || !Array.isArray(keys)) {
    return array;
  }
  
  const lowerCaseSearchTerm = searchTerm.toLowerCase();
  
  return array.filter(item => {
    return keys.some(key => {
      const value = item[key];
      if (typeof value === 'string') {
        return value.toLowerCase().includes(lowerCaseSearchTerm);
      }
      return false;
    });
  });
};
